/**
 * OptionsEditor — sortable option list for a `select`-type attribute field
 * (PIM Wave P1 Task 4, design notes Surface 2). Mounts inside
 * `FieldFormDrawer` ONLY in edit mode (a saved `field.id` exists) — options
 * are nested under a field, so there is nothing to attach them to before the
 * field itself is created (brief §5 / §1).
 *
 * Reorder: dnd-kit sortable list, mirroring
 * `seller-dashboard/PhotoReorderList.tsx` (Pointer + Keyboard sensors,
 * `closestCenter`, `arrayMove`, transform/opacity-only lift). There is NO
 * batch reorder route for options (recon §A13/§9) — a drag reassigns every
 * option's `sort_order` to its new positional index and PATCHes only the
 * options whose index actually changed vs. their previous `sort_order`
 * (brief §"Backend facts"). Optimistic: the dragged order renders
 * immediately; on any PATCH failure (final-review Finding D — corrected
 * from an earlier "the whole set rolls back" claim) only the OPTIMISTIC UI
 * reverts — `optimisticIds` is cleared and an explicit refetch re-renders
 * from server truth, plus an error toast fires — but any PATCH(es) that
 * already committed before the rejection stay persisted server-side
 * (`Promise.all` cannot undo a settled request). That can leave two
 * options sharing a `sort_order` until the next successful reorder
 * overwrites it (self-heals; never corrupts data, just briefly unstable
 * ordering).
 *
 * Per-row label edits: EACH row tracks its own trilingual draft in local
 * state and shows an explicit "Save" button once the draft differs from the
 * last-saved value (brief §2 — "a per-row save button appearing when dirty
 * ... simplest to make correct"). Toast fires ONLY on failure — a
 * successful save just re-syncs the row to the server value (silent
 * success, "the row reflects saved state").
 *
 * Archive/unarchive: dedicated POST routes (no DELETE for options at all —
 * recon §A13). Immediate, no confirm (brief §4); archived options collapse
 * into a dimmed "archived (N)" disclosure at the bottom and are excluded
 * from the sortable list.
 */
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Archive, ArchiveRestore, ChevronDown, GripVertical } from "lucide-react";

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { Button, Field } from "@garden/ui";
import { TrilingualInput, type TrilingualValue } from "@/features/admin/_shared";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { ApiError } from "@/lib/api-client";
import type { AttributeFieldOption } from "@/features/admin/attribute-fields/api";
import {
  useAttributeFieldOptions,
  useArchiveAttributeFieldOption,
  useCreateAttributeFieldOption,
  useUnarchiveAttributeFieldOption,
  useUpdateAttributeFieldOption,
} from "@/features/admin/attribute-fields/hooks";
import {
  FIELD_SLUG_MAX_LEN,
  FIELD_SLUG_PATTERN,
  slugify,
} from "@/features/admin/attribute-fields/lib/slug";

const ICON_BUTTON_CLASS =
  "grid h-9 w-9 place-items-center rounded-full text-[var(--g-secondary)] hover:bg-[var(--g-elevated)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] disabled:cursor-not-allowed disabled:opacity-40";

const EMPTY_OPTIONS: readonly AttributeFieldOption[] = [];

function toTrilingual(option: AttributeFieldOption): TrilingualValue {
  return { ru: option.label_ru, ky: option.label_ky ?? "", en: option.label_en ?? "" };
}

function sameTrilingual(draft: TrilingualValue, option: AttributeFieldOption): boolean {
  return (
    draft.ru === option.label_ru &&
    draft.ky === (option.label_ky ?? "") &&
    draft.en === (option.label_en ?? "")
  );
}

// ---------------------------------------------------------------------
// One sortable option row
// ---------------------------------------------------------------------

interface OptionRowProps {
  fieldId: string;
  option: AttributeFieldOption;
  index: number;
  dragDisabled: boolean;
}

function OptionRow({ fieldId, option, index, dragDisabled }: OptionRowProps): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: option.id,
    disabled: dragDisabled,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition ?? undefined,
    opacity: isDragging ? 0.6 : 1,
  };

  const [draft, setDraft] = useState<TrilingualValue>(() => toTrilingual(option));
  // Re-syncs from the server value ONLY when this option's OWN saved labels
  // change (e.g. after this row's own save round-trips) — a sibling row's
  // mutation invalidating the shared options query never touches these
  // deps, so an in-progress edit here is never clobbered by that refetch.
  useEffect(() => {
    setDraft(toTrilingual(option));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option.label_ru, option.label_ky, option.label_en]);

  const isDirty = !sameTrilingual(draft, option);
  const updateMutation = useUpdateAttributeFieldOption();
  const archiveMutation = useArchiveAttributeFieldOption();

  const save = (): void => {
    updateMutation.mutate(
      {
        fieldId,
        optionId: option.id,
        body: {
          label_ru: draft.ru.trim(),
          label_ky: draft.ky.trim() || null,
          label_en: draft.en.trim() || null,
        },
      },
      { onError: () => toast.error(t("error.generic")) },
    );
  };

  const archive = (): void => {
    archiveMutation.mutate(
      { fieldId, optionId: option.id },
      {
        onSuccess: () => toast.success(t("pim.options.archiveSuccess")),
        onError: () => toast.error(t("error.generic")),
      },
    );
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      data-testid="pim-option-row"
      className="flex items-start gap-2 rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-3 motion-reduce:!transition-none"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        disabled={dragDisabled}
        aria-label={t("pim.options.dragHandle", { index: index + 1 })}
        data-testid={`pim-option-drag-${option.id}`}
        className="mt-2 inline-flex size-7 shrink-0 cursor-grab items-center justify-center rounded-[var(--g-radius-sm)] text-[var(--g-secondary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] disabled:cursor-not-allowed disabled:opacity-40"
      >
        <GripVertical size={16} strokeWidth={1.75} aria-hidden />
      </button>

      <span className="mt-2.5 shrink-0 whitespace-nowrap font-mono text-xs text-[var(--g-secondary)]">
        {option.value_slug}
      </span>

      <div className="flex-1">
        <TrilingualInput
          id={`pim-option-${option.id}`}
          label={t("pim.options.rowLabel")}
          value={draft}
          onChange={setDraft}
          compact
        />
      </div>

      <div className="mt-1 flex shrink-0 items-center gap-1">
        {isDirty ? (
          <Button
            type="button"
            variant="primary"
            size="sm"
            loading={updateMutation.isPending}
            onClick={save}
            data-testid={`pim-option-save-${option.id}`}
            className="min-h-[44px]"
          >
            {t("common.save")}
          </Button>
        ) : null}
        <button
          type="button"
          data-testid={`pim-option-archive-${option.id}`}
          aria-label={t("pim.options.archive")}
          onClick={archive}
          className={ICON_BUTTON_CLASS}
        >
          <Archive size={16} strokeWidth={1.75} aria-hidden />
        </button>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------
// Archived (collapsed) row — read-only label, unarchive only
// ---------------------------------------------------------------------

function ArchivedOptionRow({ fieldId, option }: { fieldId: string; option: AttributeFieldOption }): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const unarchiveMutation = useUnarchiveAttributeFieldOption();

  const unarchive = (): void => {
    unarchiveMutation.mutate(
      { fieldId, optionId: option.id },
      {
        onSuccess: () => toast.success(t("pim.options.unarchiveSuccess")),
        onError: () => toast.error(t("error.generic")),
      },
    );
  };

  return (
    <li
      data-testid="pim-option-archived-row"
      className="flex items-center gap-2 rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-3 opacity-60"
    >
      <span className="shrink-0 whitespace-nowrap font-mono text-xs text-[var(--g-secondary)]">
        {option.value_slug}
      </span>
      <span className="flex-1 text-sm">{option.label_ru}</span>
      <button
        type="button"
        data-testid={`pim-option-unarchive-${option.id}`}
        aria-label={t("pim.options.unarchive")}
        onClick={unarchive}
        className={ICON_BUTTON_CLASS}
      >
        <ArchiveRestore size={16} strokeWidth={1.75} aria-hidden />
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------
// Add-option row
// ---------------------------------------------------------------------

interface AddOptionRowProps {
  fieldId: string;
  nextSortOrder: number;
  autoFocus: boolean;
}

function AddOptionRow({ fieldId, nextSortOrder, autoFocus }: AddOptionRowProps): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const [labelRu, setLabelRu] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);
  const ruInputRef = useRef<HTMLInputElement | null>(null);
  const createMutation = useCreateAttributeFieldOption();

  useEffect(() => {
    if (!slugTouched) setSlug(slugify(labelRu));
  }, [labelRu, slugTouched]);

  useEffect(() => {
    if (autoFocus) ruInputRef.current?.focus();
  }, [autoFocus]);

  const trimmedRu = labelRu.trim();
  const slugValid = FIELD_SLUG_PATTERN.test(slug);
  const canSubmit = trimmedRu !== "" && slugValid && !createMutation.isPending;

  const submit = (): void => {
    if (!canSubmit) return;
    setSlugError(null);
    createMutation.mutate(
      { fieldId, body: { value_slug: slug, label_ru: trimmedRu, sort_order: nextSortOrder } },
      {
        onSuccess: () => {
          setLabelRu("");
          setSlug("");
          setSlugTouched(false);
          ruInputRef.current?.focus();
        },
        onError: (err) => {
          if (err instanceof ApiError && err.code === "catalog_schema.option.duplicate_value_slug") {
            setSlugError(t("pim.options.add.slugDuplicate"));
            return;
          }
          toast.error(t("error.generic"));
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-2 rounded-[var(--g-radius-sm)] border border-dashed border-[var(--g-hairline)] p-3" data-testid="pim-option-add-row">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex-1">
          <Field
            ref={ruInputRef}
            label={t("pim.options.add.ruLabel")}
            data-testid="pim-option-add-ru"
            value={labelRu}
            onChange={(event) => setLabelRu(event.target.value)}
          />
        </div>
        <div className="flex-1">
          <Field
            label={t("pim.options.add.slugLabel")}
            data-testid="pim-option-add-slug"
            hint={slugError ? undefined : t("pim.options.add.slugHint")}
            error={slugError ?? (slug !== "" && !slugValid ? t("pim.fields.form.formError.slugPattern") : undefined)}
            value={slug}
            maxLength={FIELD_SLUG_MAX_LEN}
            onChange={(event) => {
              setSlugTouched(true);
              setSlugError(null);
              setSlug(event.target.value);
            }}
          />
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leading="plus"
          loading={createMutation.isPending}
          disabled={!canSubmit}
          onClick={submit}
          data-testid="pim-option-add-submit"
          className="min-h-[44px]"
        >
          {t("pim.options.add.submit")}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// OptionsEditor shell
// ---------------------------------------------------------------------

export interface OptionsEditorProps {
  fieldId: string;
  /** Focuses the add-option RU input on mount — set when the drawer just
   * transitioned from create -> edit mode for a `select` field (brief §5). */
  autoFocusAdd?: boolean;
}

export function OptionsEditor({ fieldId, autoFocusAdd = false }: OptionsEditorProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const { data, isLoading, isError, refetch } = useAttributeFieldOptions(fieldId);
  const options = data?.options ?? EMPTY_OPTIONS;

  const active = useMemo(
    () => options.filter((o) => !o.is_archived).slice().sort((a, b) => a.sort_order - b.sort_order),
    [options],
  );
  const archived = useMemo(
    () => options.filter((o) => o.is_archived).slice().sort((a, b) => a.sort_order - b.sort_order),
    [options],
  );

  const [showArchived, setShowArchived] = useState(false);
  const [optimisticIds, setOptimisticIds] = useState<string[] | null>(null);
  const [pendingReorder, setPendingReorder] = useState(false);
  const updateMutation = useUpdateAttributeFieldOption();

  const displayedActive = useMemo(() => {
    if (!optimisticIds) return active;
    const byId = new Map(active.map((o) => [o.id, o]));
    return optimisticIds.map((id) => byId.get(id)).filter((o): o is AttributeFieldOption => o !== undefined);
  }, [optimisticIds, active]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event: DragEndEvent): Promise<void> => {
    const { active: activeItem, over } = event;
    if (!over || activeItem.id === over.id) return;
    const oldIndex = displayedActive.findIndex((o) => o.id === activeItem.id);
    const newIndex = displayedActive.findIndex((o) => o.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(displayedActive, oldIndex, newIndex);
    setOptimisticIds(reordered.map((o) => o.id));
    setPendingReorder(true);

    // Reindex to the new positions; PATCH only the options whose sort_order
    // actually changed vs. their previous value (no batch reorder route).
    const changed = reordered
      .map((option, index) => ({ option, nextSortOrder: index }))
      .filter(({ option, nextSortOrder }) => option.sort_order !== nextSortOrder);

    try {
      await Promise.all(
        changed.map(({ option, nextSortOrder }) =>
          updateMutation.mutateAsync({
            fieldId,
            optionId: option.id,
            body: { sort_order: nextSortOrder },
          }),
        ),
      );
    } catch {
      toast.error(t("pim.options.reorderError"));
      await refetch();
    } finally {
      setOptimisticIds(null);
      setPendingReorder(false);
    }
  };

  const nextSortOrder = options.length;

  return (
    <div className="flex flex-col gap-3" data-testid="pim-options-editor">
      {isLoading ? (
        <p className="text-sm text-[var(--g-secondary)]">{t("pim.options.loading")}</p>
      ) : isError ? (
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-[var(--g-error)]">{t("pim.options.loadError")}</p>
          <Button type="button" variant="ghost" size="sm" onClick={() => void refetch()} className="min-h-[44px]">
            {t("pim.options.retry")}
          </Button>
        </div>
      ) : (
        <>
          {displayedActive.length === 0 ? (
            <p className="text-sm text-[var(--g-secondary)]">{t("pim.options.empty")}</p>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => void handleDragEnd(e)}>
              <SortableContext items={displayedActive.map((o) => o.id)} strategy={verticalListSortingStrategy}>
                <ul className="flex flex-col gap-2" data-testid="pim-options-list">
                  {displayedActive.map((option, index) => (
                    <OptionRow
                      key={option.id}
                      fieldId={fieldId}
                      option={option}
                      index={index}
                      dragDisabled={pendingReorder}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          )}

          {archived.length > 0 ? (
            <div className="flex flex-col gap-2">
              <button
                type="button"
                data-testid="pim-options-archived-toggle"
                aria-expanded={showArchived}
                onClick={() => setShowArchived((v) => !v)}
                className="flex min-h-[44px] items-center gap-1.5 self-start text-sm text-[var(--g-secondary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
              >
                <ChevronDown
                  size={16}
                  strokeWidth={1.75}
                  aria-hidden
                  className={showArchived ? "rotate-180 transition-transform" : "transition-transform"}
                />
                {t("pim.options.archivedToggle", { count: archived.length })}
              </button>
              {showArchived ? (
                <ul className="flex flex-col gap-2" data-testid="pim-options-archived-list">
                  {archived.map((option) => (
                    <ArchivedOptionRow key={option.id} fieldId={fieldId} option={option} />
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </>
      )}

      <AddOptionRow fieldId={fieldId} nextSortOrder={nextSortOrder} autoFocus={autoFocusAdd} />
    </div>
  );
}

export default OptionsEditor;
